// modules
import SearchService from 'modules/Search';
import LiveDataService from 'modules/LiveData';
import FormatService from 'modules/Format';
import { isEqual } from 'lodash';
import Validation from 'modules/Validation';
import InstrumentData from 'modules/InstrumentData';
import Fetch from 'modules/Fetch';
import { decomposeExchangeToken } from 'modules/BOD/modules/Search/utils';
// consts
import { MARKET_FEEDER_MODES } from 'modules/LiveData/constants';
import { DATA_SETTINGS, OI_ANALYSIS_INDICES, spotRowDummyData } from './consts';
// store
import OptionChainStore from './store';
// utils
import { getSelectedExpiry } from './utils';

class OptionChainService extends OptionChainStore {
  setOptionChainData = async (exchangeToken: string, isRefresh = false) => {
    const data = await SearchService.searchByExchangeToken(exchangeToken);
    this.setScripData(data);

    const updatedData = {
      instrumentKey: data?.instrumentKey || '',
      exchangeToken: data?.underlyingExchangeToken || '',
      displayName: data?.name || '',
      optionsAvailable: data?.optionsAvailable,
    };

    if (data) {
      if (!Validation.isCommodity(data?.segment)) {
        if (data.underlyingExchangeToken) {
          const scripData = await SearchService.searchByExchangeToken(
            data.underlyingExchangeToken,
          );
          Object.assign(updatedData, {
            instrumentKey:
              scripData?.instrumentKey || updatedData.instrumentKey,
            exchangeToken:
              scripData?.exchangeToken || updatedData.exchangeToken,
            displayName: scripData?.displayName || updatedData.displayName,
            optionsAvailable:
              scripData?.optionsAvailable || updatedData.optionsAvailable,
          });
        }
        this.setSpotScripData(updatedData);
      }

      const spotToken = data.underlyingExchangeToken || data.exchangeToken;

      const futureScrip = await SearchService.getFutureScripForExpiry(
        spotToken,
      );

      // Set current future scrip of the option
      this.setFutureScrip(futureScrip);

      const expiriesWithWeekly: OptionExpirySchema[] = await this.fetchExpiries(
        data?.exchangeToken ?? data?.underlyingExchangeToken ?? '',
        spotToken,
      );

      const expiries = expiriesWithWeekly.map(expiry => expiry.date);
      if (updatedData?.optionsAvailable && expiries.length > 0) {
        this.setIsOptionChainAvailable(true);
      } else {
        this.setIsOptionChainAvailable(false);
      }

      let selectedExpiry = expiries.length ? expiries[0] : null;

      if (data?.series === 'FO') {
        const date = FormatService.getTimestampFromDataSchema(data);
        selectedExpiry = date;
      }

      this.setExpiries(expiries);
      const symbol = data?.underlyingSymbol || data?.symbol || '';
      this.setSymbol(symbol);
      if (!isRefresh) {
        this.setSelectedExpiry(selectedExpiry);
      }
      this.setSpotToken(spotToken);
    }
  };

  private fetchExpiries = (exchangeToken: string, spotToken: string) =>
    SearchService.getOptionExpiries(exchangeToken, spotToken);

  updateOptionStrikes = async (
    selectedExpiry: number | null,
    spotToken: string,
  ) => {
    const { expiries } = this;
    const { segment } = decomposeExchangeToken(spotToken);
    const isCommodity = Validation.isCommodity(segment);

    if (selectedExpiry && spotToken) {
      // clear on expiry change
      this.clearOnExpiryChange();

      const selectedExpiryDate = getSelectedExpiry(
        expiries,
        selectedExpiry,
        isCommodity,
      );

      const result = await SearchService.getOptionStrikesForExpiry(
        spotToken,
        selectedExpiryDate,
      );

      // For commodity scrips, need to fetch assetKey for spot price whenever
      // expiry changes and strikes are updated as spot price will be of next fut scrip
      if (isCommodity) {
        // reset spot scrip data
        this.setSpotScripData(null);
        // set spot scrip data
        await this.setSpotScripDataForCommodity(result);
      }

      // reset amount to default
      const strikesToShow =
        this.tableSettings.dataSettings.noOfStrikes === DATA_SETTINGS.showAll
          ? Math.min(60, result?.length)
          : 20;
      this.setStrikesAmountToShow(strikesToShow);
      this.setStrikes(result);

      // Update scrip data on expiry change since lot size is different for different expiries
      const exchangeToken = this.findValidExchangeToken(result);
      if (exchangeToken) {
        const data = await SearchService.searchByExchangeToken(exchangeToken);
        this.setScripData(data);
      }
    }
  };

  findValidExchangeToken = (strikes: StrikeSchema[]) => {
    let exchangeToken = '';
    strikes.some(strike => {
      const { CE, PE } = strike.exchangeTokens || {};
      if (CE !== '') {
        exchangeToken = CE;
        return true;
      }
      if (PE !== '') {
        exchangeToken = PE;
        return true;
      }
      return false;
    });
    return exchangeToken;
  };

  setSpotScripDataForCommodity = async (strikes: StrikeSchema[]) => {
    const strikesResult: StrikeSchema[] = strikes || [];

    // For commodity scrips, need to fetch assetKey for spot price whenever
    // expiry changes and strikes are updated as spot price will be of next fut scrip
    if (strikesResult?.length > 0) {
      const strikeExchangeToken = strikesResult?.[0]?.exchangeTokens?.CE;

      const [instrumentData] = await InstrumentData.getInstrumentData(
        strikeExchangeToken,
      );

      const updatedData = {
        instrumentKey:
          instrumentData?.assetKey || instrumentData?.instrumentKey,
        exchangeToken: instrumentData?.exchangeToken || '',
        displayName: this.scripData?.name,
        optionsAvailable: this.scripData?.optionsAvailable,
      };

      this.setSpotScripData(updatedData);
    }
  };

  updateActiveStrikesIfNeeded = (
    allStrikes: StrikeSchema[],
    spotPrice: number,
    amountToShow: number,
  ) => {
    if (!allStrikes.length) return;

    // check amountToShow with current active strikes length
    const isAmountChanges = amountToShow !== this.activeStrikes.length;

    // check is spot price should change position
    const isSpotPricePositionActual =
      this.allStrikesSpotIndex !== -1 &&
      this.strikes[this.allStrikesSpotIndex]?.strike <= spotPrice &&
      this.strikes[this.allStrikesSpotIndex + 1]?.strike >= spotPrice;

    // check if we need to recalculate active strikes
    if (!isAmountChanges && isSpotPricePositionActual) return;

    let activeStrikes = [];

    const middleIndex = allStrikes.findIndex(
      (item, index, array) =>
        item?.strike <= spotPrice && array[index + 1]?.strike >= spotPrice,
    );

    this.setAllStrikesSpotIndex(middleIndex);

    if (allStrikes.length <= amountToShow) {
      activeStrikes = allStrikes;
    } else {
      const beforeSpotAmount = Math.floor(amountToShow / 2) - 1;

      const startIndex = Math.max(middleIndex - beforeSpotAmount, 0);
      const endIndex = Math.min(startIndex + amountToShow, allStrikes.length);

      activeStrikes = allStrikes.slice(startIndex, endIndex);
    }

    this.updateActiveStrikes(activeStrikes);
  };

  private getTokensForStrikes = (strikes: StrikeSchema[]) =>
    strikes.reduce(
      (prev: string[], curr) => [
        ...prev,
        curr.instrumentKeys.CE,
        curr.instrumentKeys.PE,
      ],
      [],
    );

  unsubscribeOptionChainScrips = () => {
    const tokensToUnsubscribe = this.getTokensForStrikes(this.activeStrikes);
    // unsubscribe strikes
    LiveDataService.unsubscribeScrip(
      tokensToUnsubscribe,
      MARKET_FEEDER_MODES.OPTION_CHAIN,
    );
  };

  private updateActiveStrikes(newStrikes: StrikeSchema[]) {
    const oldTokens = this.getTokensForStrikes(this.activeStrikes);
    const newTokens = this.getTokensForStrikes(newStrikes);
    this.setActiveTokens(newTokens);

    // tokens that present in previous strikes and absent in new strikes
    const tokensToUnsubscribe = oldTokens.filter(
      token => !newTokens.includes(token),
    );

    // tokens that present in new strikes and absent in previous strikes
    const tokensToSubscribe = newTokens.filter(
      token => !oldTokens.includes(token),
    );

    // unsubscribe from old strikes
    LiveDataService.unsubscribeScrip(
      tokensToUnsubscribe,
      MARKET_FEEDER_MODES.OPTION_CHAIN,
    );

    // subscribe to new strikes
    LiveDataService.subscribeScrip(
      tokensToSubscribe,
      MARKET_FEEDER_MODES.OPTION_CHAIN,
    );

    if (!isEqual(newStrikes, this.activeStrikes)) {
      this.setActiveStrikes(newStrikes);
    }
  }

  getTableDataForActiveStrikes = (
    activeStrikes: StrikeSchema[],
    spotPrice: number,
  ): OptionChainTableData => {
    const calls: optionChainTableData[] = [];
    const inCalls: optionChainTableData[] = [];
    const puts: optionChainTableData[] = [];
    const inPuts: optionChainTableData[] = [];

    const strikes: string[] = [];
    const inStrikes: string[] = [];

    const activeStrikesUp = activeStrikes.filter(
      item => item?.strike <= spotPrice,
    );

    const activeStrikesDown = activeStrikes.filter(
      item => item?.strike >= spotPrice,
    );

    activeStrikesUp.forEach(strikeUp => {
      const {
        CE: ceExchangeToken,
        PE: peExchangeToken,
      } = strikeUp.exchangeTokens;
      const {
        CE: ceInstrumentKey,
        PE: peInstrumentKey,
      } = strikeUp.instrumentKeys;

      const decimalCount = FormatService.countDecimals(
        strikeUp?.strike?.toString(),
      );
      inStrikes.push(
        FormatService.formatToIndianFloat(strikeUp?.strike, decimalCount),
      );
      inCalls.push({
        exchangeToken: ceExchangeToken,
        instrumentkey: ceInstrumentKey,
      });
      puts.push({
        exchangeToken: peExchangeToken,
        instrumentkey: peInstrumentKey,
      });
    });

    activeStrikesDown.forEach(strikeDown => {
      const {
        CE: ceExchangeToken,
        PE: peExchangeToken,
      } = strikeDown.exchangeTokens;
      const {
        CE: ceInstrumentKey,
        PE: peInstrumentKey,
      } = strikeDown.instrumentKeys;
      const decimalCount = FormatService.countDecimals(
        strikeDown?.strike?.toString(),
      );
      strikes.push(
        FormatService.formatToIndianFloat(strikeDown?.strike, decimalCount),
      );
      calls.push({
        exchangeToken: ceExchangeToken,
        instrumentkey: ceInstrumentKey,
      });
      inPuts.push({
        exchangeToken: peExchangeToken,
        instrumentkey: peInstrumentKey,
      });
    });

    return { calls, inCalls, puts, inPuts, strikes, inStrikes };
  };

  public calculateMaxOi = (liveData: string[]) => {
    const maxOI = liveData.reduce(
      (prev, curr) => (curr ? Math.max(prev, Number(curr)) : prev),
      0,
    );

    this.setMaxOI(maxOI);
  };

  public calculateMaxTotalVolume = (liveData: string[]) => {
    const maxTotalVolume = liveData.reduce(
      (prev, curr) => (curr ? Math.max(prev, Number(curr)) : prev),
      0,
    );

    this.setMaxTotalVolume(maxTotalVolume);
  };

  public handleLoadMoreData = (strikesAmountToShow: number) => {
    this.setStrikesAmountToShow(
      Math.min(this.strikes.length, strikesAmountToShow),
    );
  };

  // method to position spot at the right place between the strikes
  updateStrikesWithSpot(spotPrice: number): void {
    const tableStrikes: StrikeSchema[] = [];
    let spotInserted = false;

    // insert spot row between the strikes by comparing strike and spotPrice
    this.activeStrikes.forEach((item, index) => {
      if (!spotInserted && spotPrice < item.strike) {
        tableStrikes.push(spotRowDummyData as StrikeSchema);
        spotInserted = true;
        this.setSpotIndex(index);
      }
      tableStrikes.push({
        ...item,
        rowType: spotInserted ? 'afterSpot' : 'beforeSpot',
      });
    });

    if (!spotInserted) {
      tableStrikes.push(spotRowDummyData as StrikeSchema);
      this.setSpotIndex(tableStrikes.length);
    }

    // update active strikes, callTableData and putTableData when data changes
    if (!isEqual(tableStrikes, this.activeStrikesWithSpot)) {
      this.setActiveStrikesWithSpot(tableStrikes);
      this.updateOptionChainData(tableStrikes);
    }
  }

  // method to generate callTableData and putTableData
  updateOptionChainData = (activeStrikesWithSpot: StrikeSchema[]) => {
    const callTableData: OptionChainRowData[] = [];
    const putTableData: OptionChainRowData[] = [];

    activeStrikesWithSpot.forEach((item, index, self) => {
      const { segment: callSegment, token: callToken } = decomposeExchangeToken(
        item.exchangeTokens.CE,
      );
      const { segment: putSegment, token: putToken } = decomposeExchangeToken(
        item.exchangeTokens.PE,
      );

      // identifying itm and otm rows
      const getItmAtm = (type: OptionTypes) => {
        if (
          item.rowType === 'beforeSpot' &&
          self[index + 1].rowType === 'spot'
        ) {
          return 'atm';
        }
        if (
          (item.rowType === 'beforeSpot' && type === 'CE') ||
          (item.rowType === 'afterSpot' && type === 'PE')
        ) {
          return 'itm';
        }
        return 'otm';
      };

      const callRowData: OptionChainRowData = {
        strike: item.strike,
        rowType: item.rowType === 'spot' ? item.rowType : getItmAtm('CE'),
        exchangeToken: item.exchangeTokens.CE,
        instrumentKey: item.instrumentKeys.CE,
        segment: callSegment,
        token: callToken,
      };

      const putRowData: OptionChainRowData = {
        strike: item.strike,
        rowType: item.rowType === 'spot' ? item.rowType : getItmAtm('PE'),
        exchangeToken: item.exchangeTokens.PE,
        instrumentKey: item.instrumentKeys.PE,
        segment: putSegment,
        token: putToken,
      };

      callTableData.push(callRowData);
      putTableData.push(putRowData);
    });

    if (
      !(
        isEqual(callTableData, this.callTableData) &&
        isEqual(putTableData, this.putTableData)
      )
    ) {
      this.setRevampedOptionChainData(callTableData, putTableData);
    }
  };

  // PCR and Max Pain Data
  getOIPCROrMaxPainData = async (
    type: string,
    scripSymbol: string,
    daysRange: number,
    expiryDate: string,
  ) => {
    const subTabType = type === 'maxPain' ? '/max-pain' : '/pcr';
    const exchange = OI_ANALYSIS_INDICES[scripSymbol];
    if (!exchange) {
      return null;
    }
    const response = await Fetch.request<
      'OPTION_CHAIN',
      OiPCRDataResponseType | OiMaxPainDataResponseType
    >(
      'OPTION_CHAIN',
      subTabType,
      type === 'maxPain' ? 'GET_OPTION_CHAIN_MAX_PAIN' : 'GET_OPTION_CHAIN_PCR',
      {
        pathParameter: `${exchange}/${scripSymbol}/${expiryDate}/${daysRange}`,
      },
    );

    if (response.success) {
      return response.data;
    }

    return null;
  };

  getOptionChainData = (type: OptionTypes): OptionChainRowData[] => {
    const {
      callTableData: ceDummyData,
      putTableData: peDummyData,
    } = this.getOptionChainDummyData();

    // use dummy data to render table for first time
    if (this.loading) {
      if (type === 'CE') return ceDummyData;
      return peDummyData;
    }

    if (type === 'CE') return this.callTableData;
    return this.putTableData;
  };

  getOptionChainDummyData = () => {
    const callTableData: OptionChainRowData[] = [];
    const putTableData: OptionChainRowData[] = [];

    // generating dummy data
    for (let count = 1; count < 14; count += 1) {
      const key = count.toString();
      const rowData = {
        strike: count,
        segment: this.scripData?.segment,
        token: this.scripData?.token,
        instrumentKey: this.scripData?.instrumentKey,
        exchangeToken: key,
      };

      callTableData.push(rowData as OptionChainRowData);
      putTableData.push(rowData as OptionChainRowData);
    }

    const spotRowData: OptionChainRowData = {
      strike: 15,
      rowType: 'spot',
      segment: this.scripData?.segment,
      instrumentKey: this.scripData?.instrumentKey as string,
      token: this.scripData?.token as string,
      exchangeToken: '15',
    };

    // inserting spotRow in between the callTableData and putTableData
    callTableData.splice(7, 0, spotRowData);
    putTableData.splice(7, 0, spotRowData);

    return { callTableData, putTableData };
  };
}

export default new OptionChainService();
